/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {ErrorType, ResponseData, showDialog} from '@dvrd/dvr-controls';
import Invoice from "../../models/invoiceModel";
import {getCountryCode} from "../../util/countryUtil";
import {createReverseCharge, sendInvoices} from "../../util/requestSender";
import {onShowConfirmSnackbar} from "../../util/renderUtil";
import {createErrorMessage} from "../../util/utils";
import ReverseCharge from "./reverseCharge";
import {ActivateHandle} from "../../util/interfaces";

interface Props {
    onSuccess: VoidFunction;
}

const MAX_VALIDATION_ATTEMPTS: number = 3, SUBMIT_TIMEOUT = 1000;

function ReverseChargeController(props: Props, ref: ForwardedRef<ActivateHandle<Invoice>>) {
    const [vatNumber, setVatNumber] = useState('');
    const [country, setCountry] = useState('België');
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const invoice = useRef<Invoice>(new Invoice());
    const submitTimeout = useRef<number | null>(null);

    function onActivate(_invoice: Invoice) {
        invoice.current = _invoice;
        setVatNumber(_invoice.vatNumber);
        setCountry(_invoice.country);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setVatNumber('');
        setCountry('België');
        setLoading(false);
        setError(null);
    }

    function onChangeVatNumber(value: string) {
        setVatNumber(value);
        onResetError();
    }

    function onChangeCountry(country: string) {
        setCountry(country);
        onResetError();
    }

    function onResetError() {
        setError(null);
    }

    function onSubmit(currentAttempt: number = 1, skipValidation: boolean = false) {
        return function () {
            if (!validateValues()) return;
            setLoading(true);
            createReverseCharge({
                data: {
                    country,
                    account_id: invoice.current.accountId,
                    invoice_id: invoice.current.id,
                    vat_number: vatNumber,
                    skip_validation: skipValidation
                }, callback: (data: ResponseData) => {
                    const {success, message, error} = data;
                    if (success || message !== 'validation_timeout')
                        setLoading(false);
                    if (success) onSuccess(data);
                    else if (message === 'validation_timeout') onValidationTimeout(currentAttempt)
                    else if (error) setError(error);
                    else
                        showDialog(createErrorMessage(message ??
                            'Het aanmaken van de verlegde BTW factuur is niet gelukt.'), 'BTW verlegging mislukt');
                }
            });
        }
    }

    function onValidationTimeout(currentAttempt: number) {
        if (currentAttempt >= MAX_VALIDATION_ATTEMPTS) {
            showDialog('We kunnen het BTW nummer niet valideren omdat de VIES service tijdelijk niet bereikbaar is. ' +
                'Hierdoor kunnen wij niet garanderen dat het BTW nummer correct is, wil je toch doorgaan (eigen risico)?', 'Validatie mislukt',
                ['Nee', {label: 'Ja', onClick: onSubmit(currentAttempt + 1, true), primary: false}]);
        } else submitTimeout.current = window.setTimeout(onSubmit(currentAttempt + 1), SUBMIT_TIMEOUT);
    }

    function onSuccess(data: ResponseData) {
        if (!invoice.current.isSent) return onDenySend();
        const {credit_invoice, reversed_invoice} = data;
        showDialog('De huidige factuur is gecrediteerd en een nieuwe (verlegde) factuur is aangemaakt. Wil je de ' +
            'creditfactuur en nieuwe factuur naar de klant verzenden?', 'Factuur aangemaakt',
            [{label: 'Nee', primary: false, onClick: onDenySend}, {
                label: 'Ja',
                onClick: onSendInvoices([credit_invoice.id, reversed_invoice.id])
            }]);
    }

    function onDenySend() {
        onClose();
        onShowConfirmSnackbar('De verlegde BTW factuur is aangemaakt');
        props.onSuccess();
    }

    function onSendInvoices(invoiceIds: string[]) {
        return function () {
            setLoading(true);
            sendInvoices({
                data: {invoice_ids: invoiceIds, account_id: invoice.current.accountId, send_now: true},
                callback: (data: ResponseData) => {
                    onClose();
                    const {success, message} = data;
                    if (success) {
                        onShowConfirmSnackbar('De facturen zijn verzonden');
                        props.onSuccess();
                    } else
                        showDialog(createErrorMessage(message ?? 'Het verzenden van de facturen is niet gelukt.'),
                            'Verzenden mislukt');
                }
            });
        }
    }

    function validateValues(): boolean {
        let error: ErrorType = null;
        if (!vatNumber) {
            error = 'Vul het BTW nummer in';
        } else if (!country) {
            error = 'Kies het land';
        } else {
            const countryCode = getCountryCode(country);
            if (!vatNumber.startsWith(countryCode))
                error = 'Het BTW nummer moet beginnen met de landcode';
        }
        if (error) setError(error);
        return !error;
    }

    function clearTimeout() {
        if (submitTimeout.current !== null) {
            window.clearTimeout(submitTimeout.current);
            submitTimeout.current = null;
        }
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    useEffect(() => {
        return function () {
            clearTimeout();
        }
    }, []);

    return (
        <ReverseCharge onChangeVatNumber={onChangeVatNumber} onChangeCountry={onChangeCountry} onSubmit={onSubmit()}
                       onClose={onClose} onResetError={onResetError} error={error} vatNumber={vatNumber}
                       country={country} active={active} loading={loading}/>
    )
}

export default forwardRef<ActivateHandle, Props>(ReverseChargeController);